(function () {
  const CAROUSEL_SELECTOR = "[data-sub-collections-swiper]";
  const PRESERVE_SELECTOR = "[data-sub-collections-preserve]";

  let pendingSectionId = null;
  let preservedSubCollections = null;

  function readNumber(value, fallback) {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function readBoolean(value) {
    return value === "true" || value === true;
  }

  function getSwiperConstructor() {
    const globalScope = typeof globalThis !== "undefined" ? globalThis : window;
    return typeof globalScope.Swiper === "function" ? globalScope.Swiper : null;
  }

  function clampSlidesPerView(value, slideCount) {
    return Math.min(Math.max(value, 1), slideCount);
  }

  function buildBreakpoints(config, slideCount) {
    if (config.stackOnDesktop) {
      return {};
    }

    if (config.desktopResponsiveSlides) {
      return {
        600: {
          slidesPerView: clampSlidesPerView(
            config.slidesPerViewDesktop - 4,
            slideCount,
          ),
          spaceBetween: config.spaceBetween,
        },
        800: {
          slidesPerView: clampSlidesPerView(
            config.slidesPerViewDesktop - 3,
            slideCount,
          ),
          spaceBetween: config.spaceBetween,
        },
        1000: {
          slidesPerView: clampSlidesPerView(
            config.slidesPerViewDesktop - 1,
            slideCount,
          ),
          spaceBetween: config.spaceBetween,
        },
        1400: {
          slidesPerView: clampSlidesPerView(
            config.slidesPerViewDesktop,
            slideCount,
          ),
          spaceBetween: config.spaceBetween,
        },
      };
    }

    return {
      1000: {
        slidesPerView: config.slidesPerViewDesktop,
        spaceBetween: config.spaceBetween,
      },
    };
  }

  function readConfig(carouselEl) {
    const dataset = carouselEl.dataset;

    return {
      sectionId: dataset.sectionId,
      carouselId: dataset.carouselId,
      scopeClass: dataset.scopeClass,
      slidesPerViewMobile: readNumber(dataset.slidesPerViewMobile, 1),
      slidesPerViewDesktop: readNumber(dataset.slidesPerViewDesktop, 3),
      spaceBetweenMobile: readNumber(dataset.spaceBetweenMobile, 10),
      spaceBetween: readNumber(dataset.spaceBetween, 10),
      loop: readBoolean(dataset.loop),
      showNavigation: readBoolean(dataset.showNavigation),
      desktopResponsiveSlides: readBoolean(dataset.desktopResponsiveSlides),
      stackOnDesktop: readBoolean(dataset.stackOnDesktop),
    };
  }

  function createSwiper(carouselEl) {
    const SwiperCtor = getSwiperConstructor();

    if (!SwiperCtor || carouselEl.swiper) {
      return carouselEl.swiper ?? null;
    }

    const config = readConfig(carouselEl);
    const section = document.getElementById(
      `shopify-section-${config.sectionId}`,
    );
    const scope = config.scopeClass
      ? section?.querySelector(`.${CSS.escape(config.scopeClass)}`)
      : null;
    const wrapperEl = document.getElementById(config.carouselId);

    if (!wrapperEl) {
      return null;
    }

    const slides = wrapperEl.querySelectorAll(".swiper-slide");

    if (slides.length === 0) {
      return null;
    }

    carouselEl.classList.add("swiper-not-initialized");

    const searchRoot = scope || section;
    const nextButton = searchRoot?.querySelector(
      `[data-carousel-id="${config.carouselId}"].slider-button--next`,
    );
    const prevButton = searchRoot?.querySelector(
      `[data-carousel-id="${config.carouselId}"].slider-button--prev`,
    );
    const slideCount = slides.length;
    const swiperConfig = {
      slidesPerView: config.slidesPerViewMobile,
      spaceBetween: config.spaceBetweenMobile,
      resizeObserver: config.desktopResponsiveSlides || config.stackOnDesktop,
      loop: config.loop && slideCount > config.slidesPerViewMobile,
      breakpoints: buildBreakpoints(config, slideCount),
    };

    if (config.showNavigation && (nextButton || prevButton)) {
      swiperConfig.navigation = {
        nextEl: nextButton,
        prevEl: prevButton,
      };
    }

    try {
      const swiper = new SwiperCtor(carouselEl, swiperConfig);
      carouselEl.classList.remove("swiper-not-initialized");
      return swiper;
    } catch (error) {
      console.error("Sub collections Swiper initialization error:", error);
      carouselEl.classList.remove("swiper-not-initialized");
      return null;
    }
  }

  function refreshSwiper(carouselEl) {
    if (!carouselEl) {
      return;
    }

    if (carouselEl.swiper) {
      carouselEl.classList.remove("swiper-not-initialized");
      carouselEl.swiper.update();
      carouselEl.swiper.navigation?.update?.();
      return;
    }

    createSwiper(carouselEl);
  }

  function initSwiper(carouselEl) {
    function run() {
      if (!getSwiperConstructor()) {
        return false;
      }

      requestAnimationFrame(() => {
        createSwiper(carouselEl);
      });

      return true;
    }

    if (run()) {
      return;
    }

    let tries = 0;
    const intervalId = window.setInterval(() => {
      tries += 1;

      if (run() || tries > 60) {
        window.clearInterval(intervalId);
      }
    }, 50);
  }

  function boot(root = document) {
    root.querySelectorAll(CAROUSEL_SELECTOR).forEach((carouselEl) => {
      if (!carouselEl.swiper) {
        initSwiper(carouselEl);
      }
    });
  }

  function destroyInRoot(root) {
    root.querySelectorAll(CAROUSEL_SELECTOR).forEach((carouselEl) => {
      if (
        carouselEl.swiper &&
        typeof carouselEl.swiper.destroy === "function"
      ) {
        carouselEl.swiper.destroy(true, true);
      }
    });
  }

  function preserveSubCollections(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    const subCollections = section?.querySelector(PRESERVE_SELECTOR);

    if (!subCollections) {
      preservedSubCollections = null;
      return;
    }

    preservedSubCollections = subCollections;
    subCollections.remove();
  }

  function restoreSubCollections(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);

    if (!section) {
      preservedSubCollections = null;
      return;
    }

    if (preservedSubCollections) {
      const collectionMain = section.querySelector(".collection__main");
      const replacement = section.querySelector(PRESERVE_SELECTOR);

      if (collectionMain) {
        if (replacement) {
          replacement.replaceWith(preservedSubCollections);
        } else {
          collectionMain.insertBefore(
            preservedSubCollections,
            collectionMain.firstChild,
          );
        }

        preservedSubCollections
          .querySelectorAll(CAROUSEL_SELECTOR)
          .forEach(refreshSwiper);
      }

      preservedSubCollections = null;
      return;
    }

    boot(section);
  }

  function scheduleAfterSectionRender(callback) {
    queueMicrotask(callback);
  }

  document.addEventListener(
    "facet:update",
    (event) => {
      pendingSectionId =
        event.detail?.url?.searchParams?.get("section_id") || null;
    },
    true,
  );

  document.addEventListener("facet:before-section-replace", (event) => {
    const sectionId = event.detail?.sectionId || pendingSectionId;

    if (!sectionId) {
      return;
    }

    preserveSubCollections(sectionId);
  });

  document.addEventListener("theme:loading:end", () => {
    if (!pendingSectionId) {
      return;
    }

    const sectionId = pendingSectionId;

    if (!preservedSubCollections || preservedSubCollections.isConnected) {
      pendingSectionId = null;
      preservedSubCollections = null;
      return;
    }

    scheduleAfterSectionRender(() => {
      restoreSubCollections(sectionId);
      pendingSectionId = null;
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener("load", () => boot(document));

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.(CAROUSEL_SELECTOR)) {
      boot(event.target);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyInRoot(event.target);
  });
})();
